import { ApiService, EnterpriseAccountRequest, HttpStatus, isHttpError } from "@/api";
import { makeAutoObservable } from "mobx";
import { AxiosInstance } from "axios";
import { logDebug } from "@/utils/log";
import UserStore from "@/stores/UserStore";
import { AcceptCompanyTermsDialogModel } from "@/termOfService/TermsOfServiceDialog";
import { History } from "history";
import {
  BillingFrequency,
  billingFrequencyLabels,
  BR_STATUS,
  emailValidator,
  EnterpriseAccount,
  EnterpriseAccountDetails,
  EnterpriseAccountStatus,
  failure,
  isOk,
  ok,
  pending,
  Portfolio,
  Result,
} from "@/common/common";
import { navigateToPortfolio, navigateToUserPortfolio } from "@/utils/navigation";
import {
  ActivateSubscriptionController,
  ActiveShareholderRegisterSubscriptionContext,
  purchasableProducts,
  SubscriptionTypeSelectionController,
} from "@/payment/Payment";
import { PaymentService } from "@/payment/PaymentApi";
import {
  OpenSendInvitationDialogParams,
  SendInvitationDialogStore,
} from "@/stores/SendInvitationDialogStore";
import {
  NewShareClassController,
  ShareClassApi,
} from "@/shareholderRegister/transactions/shareClassDomain";
import { AffiliateService } from "@/affiliate/Affiliate";
import { getFullName, validateName } from "@/utils/Roles";
import { MeetingType, meetingTypeCaption } from "@/meetings/meetingsDomain";
import { Language } from "@/i18n/languages";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { TMessage } from "@/i18n/tt";
import { InvitationType } from "@/types/Invitation";
import { EditSingleShareholderController } from "@/shareholderRegister/shareholders/editSingleShareholderDomain";
import { PortfolioCompany } from "@/stores/PortfolioStore";
import { ChangePermissionsModel, ObjectPermission } from "@/types/Permissions";
import {
  EditEmailForm,
  InviteNewAdminDialogStore,
  RevokeAllForm,
} from "@/portfolio/contacts/contactsDomain";
import {
  LocalDateFieldModel,
  NumberFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import { AIClient } from "@/ai/AIAssistant";
import { ShareType, ShareTypeCode } from "@/common/share";
import { AlertState, ConfirmState } from "@/common/alertConfirm";
import { DCEvent, EventDispatcher } from "@/events/eventsDomain";
import { Actor, ActorsSelectorModel, ActorType, SelectedActor } from "@/types/SelectActors";
import ShareholdersStore from "@/stores/ShareholdersStore";
import { ExtendedShareholderDTO } from "@/types/Shareholder";
import {
  CustomRoleDetailsModel,
  InviteUserToCustomRoleModel,
} from "@/portfolio/contacts/customRoleDomain";
import { combineDateAndTime, today, toLocalDateISOString } from "@/utils/Dates";
import {
  ExportShareholderData,
  ExportShareholderModel,
} from "@/shareholderRegister/shareholders/ExportShareholdersDomain";
import {
  ExportTransactionsData,
  ExportTransactionsModel,
} from "@/shareholderRegister/transactions/exportTransactionsDomain";
import { AccessType } from "@/types/enums/AccessType";
import { Transaction } from "@/types/Transactions";
import { FindTripletexCustomerResponse } from "@/enterpriseAccounts/enterpriseAccountsDomain";
import { DropdownMenuItemType } from "@/components-v2/Dropdown";
import { ModuleName, ProductDescription } from "@/common/product";
import { ReplayTransactionError } from "@/stores/TransactionStore";
import { ImportTransactionsResult } from "@/shareholderRegister/transactions/TransactionApiClient";
import { identity } from "lodash";
import { DuplicateProjectDialogState } from "@/dbot/duplicateProject/duplicateProjectDomain";
import {
  SaveAsTemplateDialogState,
  TemplateVisibilityDialogState,
} from "@/templates/saveTemplateDomain";
import {
  AddToSubmissionReferenceProps,
  AltinnSignerReminderProps,
  CreateSubmissionReferenceProps,
  DecisionBRStatusUpdateProps,
} from "@/backoffice/backofficeDomain";
import { ConversionProps } from "@/components-v2/FilePreview";

export class VerifyEmailController {
  verificationCodeModel: StringFieldModel;
  updatedEmailResult?: Result<TMessage, TMessage>;

  constructor(
    readonly apiService: ApiService,
    readonly emailAddress: string,
    readonly closeDialog: () => void,
  ) {
    this.verificationCodeModel = new StringFieldModel({ required: true });
    makeAutoObservable(this, { apiService: false }, { autoBind: true });
  }

  *verifyUserEmail() {
    this.verificationCodeModel.validate();
    if (!this.verificationCodeModel.isValid) return;

    try {
      this.updatedEmailResult = pending();
      yield this.apiService.verifyUserEmail(
        this.emailAddress,
        this.verificationCodeModel.validatedValue,
      );
      this.updatedEmailResult = ok({ key: "ok" });
    } catch (error) {
      if (isHttpError(HttpStatus.BAD_REQUEST, error)) {
        this.updatedEmailResult = failure({ key: "login.invalid-one-time-code" });
      } else {
        this.updatedEmailResult = failure({ key: "registerUser.updateEmailFailed" });
      }
    }
  }

  *sendVerificationEmail() {
    this.updatedEmailResult = undefined;
    yield this.apiService.sendEmailVerificationCode(this.emailAddress);
    this.verificationCodeModel.clear();
  }

  skipVerification() {
    this.closeDialog();
  }
}

export type AddNewShareClassParams = {
  shareholderStore: ShareholdersStore;
  locale: Language;
  http: AxiosInstance;
  onFinish: (newShareType: ShareType) => void;
};

type AccountSubscriptionLimitSpec = {
  productId: string;
  limit: NumberFieldModel;
};

export class EnterpriseAccountModel {
  createMode: RadioFieldModel<"import" | "manual">;
  name: StringFieldModel;
  status: RadioFieldModel<EnterpriseAccountStatus>;
  billingFrequency: RadioFieldModel<BillingFrequency>;
  startTime: LocalDateFieldModel;
  endTime: LocalDateFieldModel;
  contactName: StringFieldModel;
  internalReference: StringFieldModel;
  contactEmail: StringFieldModel;
  primaryCompanyOrgNo: StringFieldModel;
  keyAccountManager: StringFieldModel;
  hubspotLink: StringFieldModel;
  tripletexLink: StringFieldModel;
  findTripletexCustomerResult?: Result<FindTripletexCustomerResponse, TMessage>;
  productSpecs: AccountSubscriptionLimitSpec[] = [];
  account?: EnterpriseAccount;

  constructor(
    readonly apiService: ApiService,
    readonly messageStore: AlertMessageStore,
    readonly onSuccess: () => void,
    readonly data?: EnterpriseAccountDetails,
    readonly products?: ProductDescription[],
  ) {
    this.account = data?.account;
    this.createMode = new RadioFieldModel({
      value: "import",
      options: ["import", "manual"],
      idFn: identity,
      labelFn: (option) =>
        option === "import"
          ? { key: "enterprise-account.import-from-tripletex" }
          : { key: "enterprise-account.add-manually" },
    });
    const account = this.account;
    this.name = new StringFieldModel({ required: true, value: account?.name });
    this.internalReference = new StringFieldModel({
      required: false,
      value: account?.internalReferenceName,
    });
    this.status = new RadioFieldModel<EnterpriseAccountStatus>({
      value: account?.status ?? EnterpriseAccountStatus.ACTIVE,
      options: Object.values(EnterpriseAccountStatus),
      idFn: (value) => value,
      labelFn: (value) => ({
        key:
          value === EnterpriseAccountStatus.ACTIVE
            ? "enterprise-account.status.active"
            : "enterprise-account.status.inactive",
      }),
    });
    this.billingFrequency = new RadioFieldModel<BillingFrequency>({
      value: account?.billingFrequency ?? BillingFrequency.ANNUALLY,
      options: Object.values(BillingFrequency),
      idFn: (value) => value,
      labelFn: (value) => ({
        key: billingFrequencyLabels[value],
      }),
    });
    this.startTime = new LocalDateFieldModel({
      required: true,
      value: toLocalDateISOString(account?.startTime ? account.startTime : today()),
    });
    this.endTime = new LocalDateFieldModel({ value: account?.endTime ?? undefined });
    this.contactName = new StringFieldModel({
      validator: validateName,
      value: getFullName(account?.personInfo),
    });
    this.contactEmail = new StringFieldModel({
      validator: emailValidator,
      value: account?.personInfo?.email,
    });
    this.primaryCompanyOrgNo = new StringFieldModel({
      required: true,
      value: account?.primaryCompanyOrgNo,
    });
    this.keyAccountManager = new StringFieldModel({
      value: account?.metadata.keyAccountManager ?? undefined,
    });
    this.hubspotLink = new StringFieldModel({ value: account?.metadata.hubspotLink ?? undefined });
    this.tripletexLink = new StringFieldModel({
      value: account?.metadata.tripletexLink ?? undefined,
    });
    this.productSpecs =
      account?.subscriptionLimits?.map((s) => ({
        productId: s.productId,
        limit: new NumberFieldModel({ value: s.limit, isPositive: true }),
      })) ?? [];
    makeAutoObservable(this, { apiService: false }, { autoBind: true });
  }

  *importCustomerFromTripletex() {
    try {
      if (this.primaryCompanyOrgNo.validate()) {
        this.findTripletexCustomerResult = pending();
        const tripletexCustomer: FindTripletexCustomerResponse =
          yield this.apiService.findTripletexCustomer(this.primaryCompanyOrgNo.validatedValue);
        if (tripletexCustomer.customer) {
          this.findTripletexCustomerResult = ok(tripletexCustomer);
          this.name.setAndCommitInputValue(tripletexCustomer.customer.name);
          this.contactName.setAndCommitInputValue(tripletexCustomer.contact?.displayName ?? "");
          this.contactEmail.setAndCommitInputValue(tripletexCustomer.customer.invoiceEmail);
          this.keyAccountManager.setAndCommitInputValue(
            tripletexCustomer.accountManager?.displayName ?? "",
          );
          this.tripletexLink.setAndCommitInputValue(
            `https://tripletex.no/execute/customerMenu?contextId=23246895&customerId=${tripletexCustomer.customer.id}`,
          );
        } else {
          this.findTripletexCustomerResult = failure({ translated: "Customer not found" });
        }
      }
    } catch (e) {
      this.messageStore.showGeneralError();
      this.findTripletexCustomerResult = undefined;
    }
  }

  *save() {
    if (!this.validate()) {
      return;
    }
    try {
      if (this.account) {
        yield this.apiService.updateEnterpriseAccount(this.account.id, this.prepareData());
      } else {
        yield this.apiService.createEnterpriseAccount(this.prepareData());
      }
      this.onSuccess();
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  addProductButtons(lang: Language): DropdownMenuItemType[] {
    return (
      this.products?.map<DropdownMenuItemType>((p) => ({
        type: "item",
        label: { translated: productName(this.products, p.productId, lang) },
        onSelect: () =>
          this.productSpecs.push({
            productId: p.productId,
            limit: new NumberFieldModel({ isPositive: true }),
          }),
      })) ?? []
    );
  }

  validate() {
    this.name.validate();
    this.primaryCompanyOrgNo.validate();
    this.startTime.validate();
    this.contactName.validate();
    this.contactEmail.validate();
    return (
      this.name.isValid &&
      this.primaryCompanyOrgNo.isValid &&
      this.startTime.isValid &&
      this.contactName.isValid &&
      this.contactEmail.isValid
    );
  }

  private prepareData(): EnterpriseAccountRequest {
    return {
      name: this.name.validatedValue,
      internalReferenceName: this.internalReference.validatedValue,
      status: this.status.value,
      subscriptionLimits: this.productSpecs
        .filter((p) => p.limit.toNumberOrZero() > 0)
        .map((p) => ({
          productId: p.productId,
          limit: p.limit.toNumberOrZero(),
        })),
      startTime: combineDateAndTime(this.startTime.validatedValue!, "12:00"),
      contactName: this.contactName.inputValue,
      contactEmail: this.contactEmail.inputValue,
      primaryCompanyOrgNo: this.primaryCompanyOrgNo.validatedValue,
      metadata: {
        keyAccountManager: this.keyAccountManager.validatedValue,
        hubspotLink: this.hubspotLink.validatedValue,
        tripletexLink: this.tripletexLink.validatedValue,
      },
      billingFrequency: this.billingFrequency.value,
    };
  }

  get isNew() {
    return !this.account;
  }

  get showImport() {
    return (
      !isOk(this.findTripletexCustomerResult) && !this.account && this.createMode.value === "import"
    );
  }

  get showForm() {
    return (
      isOk(this.findTripletexCustomerResult) || this.account || this.createMode.value === "manual"
    );
  }
}

export type DialogType =
  | DuplicateProjectDialogState
  | SaveAsTemplateDialogState
  | TemplateVisibilityDialogState
  | { type: "template-project" }
  | { type: "acceptCompanyTerms"; model: AcceptCompanyTermsDialogModel }
  | { type: "payment"; model: SubscriptionTypeSelectionController }
  | { type: "sendInvitation"; model: SendInvitationDialogStore }
  | { type: "inviteNewAdmin"; model: InviteNewAdminDialogStore }
  | { type: "editEmailAdmin"; model: EditEmailForm }
  | { type: "revokeAllAdminAccess"; model: RevokeAllForm }
  | { type: "addShareType"; model: NewShareClassController }
  | { type: "activateSubscription"; model: ActivateSubscriptionController }
  | { type: "showGeneralMeetingAnnouncement"; shareholderCount: number }
  | {
      type: "activeShareholderRegisterSubscription";
      context: ActiveShareholderRegisterSubscriptionContext;
    }
  | {
      type: "newMeeting";
      model: {
        meetingType: MeetingType;
        organisationNumber: string;
        projectName: TMessage;
        onCancel: () => void;
        projectId?: string;
      };
    }
  | {
      type: "showCreatePortfolioDialog";
      apiService: ApiService;
      onSuccess: (portfolio: Portfolio) => void;
    }
  | {
      type: "preview-file";
      filename?: string;
      file: { url: string } | { data: ArrayBuffer | Blob };
      conversion?: ConversionProps;
    }
  | {
      type: "editProjectName";
      name: StringFieldModel;
      save: ((name: string) => void) | ((name: string) => DCEvent[]) | DCEvent[];
    }
  | {
      type: "verify-email";
      controller: VerifyEmailController;
    }
  | {
      type: "edit-shareholder";
      controller: EditSingleShareholderController;
      infoLabel?: TMessage;
    }
  | {
      type: "change-permissions";
      model: ChangePermissionsModel;
    }
  | {
      type: "confirm";
      state: ConfirmState;
    }
  | {
      type: "alert";
      state: AlertState;
    }
  | {
      type: "select-actors";
      model: ActorsSelectorModel;
    }
  | {
      type: "decision-approval-date";
      decisionIds: string[];
      status: BR_STATUS;
      model: LocalDateFieldModel;
    }
  | {
      type: "create-submission-reference";
      props: CreateSubmissionReferenceProps;
    }
  | {
      type: "add-to-submission-reference";
      props: AddToSubmissionReferenceProps;
    }
  | {
      type: "decision-signer-reminder";
      props: AltinnSignerReminderProps;
    }
  | {
      type: "decision-BR-status-update";
      props: DecisionBRStatusUpdateProps;
    }
  | {
      type: "submit-for-signing-success";
      orgNo?: string;
    }
  | {
      type: "enterprise-account";
      model: EnterpriseAccountModel;
    }
  | {
      type: "open-role-details";
      model: CustomRoleDetailsModel;
    }
  | {
      type: "invite-custom-role-user";
      model: InviteUserToCustomRoleModel;
    }
  | {
      type: "export-shareholders";
      model: ExportShareholderModel;
    }
  | {
      type: "export-transactions";
      model: ExportTransactionsModel;
    }
  | {
      type: "change-transaction-date";
      model: ChangeTransactionDateModel;
      onClose: () => void;
    }
  | { type: "replay-error"; orgNo: string; error: ReplayTransactionError }
  | { type: "import-result"; result: ImportTransactionsResult | null };

export class DialogStore {
  readonly dispatch: EventDispatcher;
  readonly userStore: UserStore;
  readonly apiService: ApiService;
  readonly history: History;
  readonly http: AxiosInstance;
  readonly paymentService: PaymentService;
  messageStore: AlertMessageStore;

  constructor(
    dispatch: EventDispatcher,
    userStore: UserStore,
    apiService: ApiService,
    history: History,
    http: AxiosInstance,
    messageStore: AlertMessageStore,
  ) {
    this.dispatch = dispatch;
    this.userStore = userStore;
    this.apiService = apiService;
    this.history = history;
    this.http = http;
    this.messageStore = messageStore;
    this.paymentService = new PaymentService(http);

    makeAutoObservable(
      this,
      { userStore: false, apiService: false, history: false, http: false },
      { autoBind: true },
    );
  }

  openDialog(dialog: DialogType) {
    logDebug("Dialogs.openDialog", dialog);
    this.dispatch([{ type: "show-dialog", dialog }]);
  }

  closeDialog() {
    this.dispatch([{ type: "closeDialog" }]);
  }

  openAcceptCompanyTermsDialog() {
    const onDecline = () => {
      this.closeDialog();
      navigateToPortfolio(this.history);
    };

    this.openDialog({
      type: "acceptCompanyTerms",
      model: new AcceptCompanyTermsDialogModel(
        this.http,
        this.userStore,
        this.closeDialog,
        onDecline,
      ),
    });
  }

  openSubscriptionDialog(lang: Language, userStore: UserStore, affiliateService: AffiliateService) {
    if (userStore.userRole.allowCompanySettingsEdit !== true) {
      this.messageStore.showMessage("info", {
        key: "subscription.contact-admin",
        params: { companyName: userStore.companyName ?? "" },
      });
    } else if (userStore.hasInactiveShareholderRegisterSubscription) {
      this.openActivateSubscriptionDialog(userStore);
    } else if (userStore.hasActiveShareholderRegisterSubscription) {
      this.openActiveShareholderRegisterSubscriptionDialog(userStore);
    } else {
      this.openPaymentDialog(lang, userStore, affiliateService);
    }
  }

  openCreatePortfolioDialog() {
    this.openDialog({
      type: "showCreatePortfolioDialog",
      apiService: this.apiService,
      onSuccess: (p) => {
        navigateToUserPortfolio(p.id, this.history);
        this.userStore.portfolios.push(p);
        this.userStore.refreshUserInformation();
        this.messageStore.showSuccess({ key: "portfolio.created" }, { autoHide: false });
      },
    });
  }

  openPaymentDialog(locale: Language, userStore: UserStore, affiliateService: AffiliateService) {
    const user = userStore.user;
    const companyInfo = userStore.companyInfo;
    if (user && companyInfo) {
      Promise.all([
        this.paymentService.findAvailableProducts(),
        this.paymentService.findProductSubscription(companyInfo.company.organizationnumber),
        affiliateService.getAffiliateById(companyInfo.company.affiliateId),
      ]).then(([listProducts, existingSubscription, affiliate]) => {
        const model = new SubscriptionTypeSelectionController({
          paymentService: this.paymentService,
          onCancel: () => this.closeDialog(),
          onFinish: () => this.userStore.refreshUserInformation(),
          userContext: {
            companyName: companyInfo.company.name,
            administratorName: getFullName(user),
            administratorEmail: this.userStore.user?.email ?? "",
            organisationNumber: companyInfo.company.organizationnumber,
            currentShareholderCount: Object.values(companyInfo.numberOfSharesPerShareholder).length,
            existingSubscription,
            affiliate,
          },
          locale,
          listProducts: purchasableProducts(listProducts),
        });

        this.openDialog({
          type: "payment",
          model,
        });
      });
    }
  }

  openSendInvitationDialog(params: OpenSendInvitationDialogParams) {
    this.openDialog({
      type: "sendInvitation",
      model: new SendInvitationDialogStore(
        params,
        this.apiService,
        this.messageStore,
        this.closeDialog,
        new AIClient(this.http, this.userStore.orgNumber),
        invitationEmailText(params, {
          senderName: getFullName(this.userStore.user),
          email: this.userStore.user?.email,
        }),
      ),
    });
  }

  openInviteNewAdminDialog({
    lang,
    companies,
    selectedPortfolioId,
  }: {
    lang: Language;
    companies: PortfolioCompany[];
    selectedPortfolioId?: string;
  }) {
    this.openDialog({
      type: "inviteNewAdmin",
      model: new InviteNewAdminDialogStore({
        locale: lang,
        onClose: this.closeDialog,
        companies,
        apiService: this.apiService,
        messageStore: this.messageStore,
        portfolios: this.userStore.portfolios,
        selectedPortfolioId,
      }),
    });
  }

  openAddNewShareClassDialog({ shareholderStore, locale, http, onFinish }: AddNewShareClassParams) {
    const usedCodes = new Set(shareholderStore.activeShareTypes.map((s) => s.shareTypeCode));
    const controller = new NewShareClassController(
      shareholderStore.userStore.orgNumber,
      new ShareClassApi(http),
      this.closeDialog,
      shareholderStore.shareTypeCodes
        .map((d) => d as ShareTypeCode)
        .filter((entry) => !usedCodes.has(entry)),
      locale,
      onFinish,
    );
    this.openDialog({ type: "addShareType", model: controller });
  }

  openNewMeetingDialog(
    organisationNumber: string,
    meetingType: MeetingType,
    projectName?: TMessage,
    projectId?: string,
  ) {
    this.openDialog({
      type: "newMeeting",
      model: {
        meetingType: meetingType,
        organisationNumber: organisationNumber,
        onCancel: () => {
          this.closeDialog();
        },
        projectName: projectName ?? meetingTypeCaption({ meetingType }),
        projectId: projectId,
      },
    });
  }

  openActivateSubscriptionDialog(userStore: UserStore) {
    const companyInfo = userStore.companyInfo;
    if (companyInfo) {
      this.openDialog({
        type: "activateSubscription",
        model: new ActivateSubscriptionController({
          onCloseDialog: this.closeDialog,
          companyName: companyInfo.company.name,
          organisationNumber: companyInfo.company.organizationnumber,
          http: this.http,
          onFinishDialog: () => this.userStore.refreshUserInformation(),
        }),
      });
    }
  }

  openActiveShareholderRegisterSubscriptionDialog(userStore: UserStore) {
    const companyInfo = userStore.companyInfo;
    if (companyInfo) {
      this.openDialog({
        type: "activeShareholderRegisterSubscription",
        context: { onCloseDialog: this.closeDialog, companyName: companyInfo.company.name },
      });
    }
  }

  openVerifyEmailDialog(emailAddress: string) {
    this.userStore.sendEmailVerificationCode(emailAddress);
    this.openDialog({
      type: "verify-email",
      controller: new VerifyEmailController(this.apiService, emailAddress, () => {
        this.closeDialog();
        this.userStore.refreshUserInformation();
      }),
    });
  }

  openEditShareholderDialog({
    orgNumber,
    locale,
    shareholderId,
    onSuccess,
    isShareholderView,
    infoLabel,
    loadShareholders,
  }: {
    orgNumber: string;
    locale: Language;
    onSuccess: (shareholder?: ExtendedShareholderDTO) => void;
    shareholderId?: number;
    isShareholderView?: boolean;
    infoLabel?: TMessage;
    loadShareholders: () => Promise<Actor[]>;
  }) {
    const isCustomRole = this.userStore.accessType === AccessType.CUSTOM;
    const adminCompanies = this.userStore.companies.filter(
      (c) => c.accessType === AccessType.ADMIN,
    );
    const showNationalId = this.userStore.companyInfo?.showNationalId;
    const portfolios = this.userStore.portfolios;
    this.openDialog({
      type: "edit-shareholder",
      controller: new EditSingleShareholderController({
        apiService: this.apiService,
        messageStore: this.messageStore,
        orgNumber,
        onSuccess,
        onClose: this.closeDialog,
        locale,
        shareholderId,
        isShareholderView,
        loadShareholders,
        portfolios,
        adminCompanies,
        isCustomRole,
        showNationalId,
      }),
      infoLabel,
    });
  }

  openChangePermissionDialog(
    title: string | TMessage,
    objectId: string,
    moduleName: ModuleName,
    onUpdate: (permissions: ObjectPermission[]) => void,
  ) {
    this.openDialog({
      type: "change-permissions",
      model: new ChangePermissionsModel(
        title,
        this.apiService,
        objectId,
        onUpdate,
        this.closeDialog,
        this.messageStore,
        this.userStore,
        new AIClient(this.http, this.userStore.orgNumber),
        moduleName,
      ),
    });
  }

  openGeneralMeetingReleaseDialog() {
    this.openDialog({
      type: "showGeneralMeetingAnnouncement",
      shareholderCount: Object.values(
        this.userStore.companyInfo?.numberOfSharesPerShareholder ?? [],
      ).length,
    });
  }

  openActorSelectorDialog({
    title,
    organisationNumber,
    tabs,
    excludeActors,
    onLoad,
    onSubmit,
    isMultiSelect = true,
    excludeCompanies = false,
  }: {
    title: TMessage;
    organisationNumber: string;
    tabs: ActorType[];
    excludeActors: string[];
    onLoad: (
      type: ActorType,
      onSuccess: (result: Actor[]) => DCEvent[],
      onFailure: () => DCEvent[],
    ) => void;
    onSubmit: (
      result: SelectedActor[],
      onsuccess: () => void,
      onError: (isValidationError?: boolean) => void,
    ) => void;
    isMultiSelect?: boolean;
    excludeCompanies?: boolean;
  }) {
    this.openDialog({
      type: "select-actors",
      model: new ActorsSelectorModel({
        title,
        organisationNumber,
        tabs,
        excludeActors,
        onLoad,
        onSubmit: (data, onError) => {
          onSubmit(data, this.closeDialog, (isValidationError?: boolean) => {
            onError();
            if (isValidationError) {
              this.messageStore.showError({ key: "error.informationMissingOrInvalid" });
            } else {
              this.messageStore.showGeneralError();
            }
          });
        },
        onClose: this.closeDialog,
        isMultiSelect,
        excludeCompanies,
      }),
    });
  }

  openEnterpriseAccountDialog(
    onSuccess: () => void,
    data?: EnterpriseAccountDetails,
    products?: ProductDescription[],
  ) {
    this.openDialog({
      type: "enterprise-account",

      model: new EnterpriseAccountModel(
        this.apiService,
        this.messageStore,
        () => {
          onSuccess();
          this.closeDialog();
        },
        data,
        products,
      ),
    });
  }

  openExportShareholdersDialog({
    onSubmit,
    selectedContext,
    selectedDate,
  }: {
    onSubmit: (data: ExportShareholderData, onSuccess: () => void, onFailure: () => void) => void;
    selectedContext?: string;
    selectedDate?: string;
  }) {
    const isCustomRole = this.userStore.accessType === AccessType.CUSTOM;
    const adminCompanies = this.userStore.companies.filter(
      (c) => c.accessType === AccessType.ADMIN,
    );
    const portfolios = this.userStore.portfolios;
    this.openDialog({
      type: "export-shareholders",
      model: new ExportShareholderModel(
        onSubmit,
        this.closeDialog,
        adminCompanies,
        portfolios,
        isCustomRole,
        selectedContext,
        selectedDate,
      ),
    });
  }

  openExportTransactionsDialog({
    onSubmit,
    initialLanguage,
  }: {
    onSubmit: (data: ExportTransactionsData, onSuccess: () => void, onFailure: () => void) => void;
    initialLanguage: Language;
  }) {
    this.openDialog({
      type: "export-transactions",
      model: new ExportTransactionsModel(onSubmit, this.closeDialog, initialLanguage),
    });
  }

  openChangeTransactionDateDialog({
    selectedTransaction,
    controller,
    infoText,
  }: {
    selectedTransaction: Transaction;
    controller: {
      transactionLog: Transaction[];
      doChangeTransactionDate: (transaction: Transaction, date: string) => Generator;
      cancelChangeTransactionDate: () => void;
      changeDateResult?: Result<TMessage, TMessage>;
    };
    infoText?: TMessage;
  }) {
    return this.openDialog({
      type: "change-transaction-date",
      model: new ChangeTransactionDateModel(selectedTransaction, controller, infoText),
      onClose: this.closeDialog,
    });
  }
}

class ChangeTransactionDateModel {
  selectedTransaction: Transaction;
  controller: {
    transactionLog: Transaction[];
    doChangeTransactionDate: (transaction: Transaction, date: string) => Generator;
    cancelChangeTransactionDate: () => void;
    changeDateResult?: Result<TMessage, TMessage>;
  };
  infoText?: TMessage;

  constructor(
    selectedTransaction: Transaction,
    controller: {
      transactionLog: Transaction[];
      doChangeTransactionDate: (transaction: Transaction, date: string) => Generator;
      cancelChangeTransactionDate: () => void;
      changeDateResult?: Result<TMessage, TMessage>;
    },
    infoText?: TMessage,
  ) {
    this.selectedTransaction = selectedTransaction;
    this.controller = controller;
    this.infoText = infoText;
  }
}

export function productName(
  products: ProductDescription[] | undefined,
  productId: string,
  lang: Language,
) {
  return (
    products
      ?.find((product) => product.productId === productId)
      ?.productLanguages?.find((l) => l.language === lang)?.name ?? ""
  );
}

type InvitationSender = {
  senderName: string;
  email?: string;
};

const invitationEmailText = (
  params: OpenSendInvitationDialogParams,
  invitationSender: InvitationSender,
): TMessage => {
  const sender = { ...invitationSender, companyName: params.companyName };
  switch (params.invitationType) {
    case InvitationType.SHAREHOLDER:
      return { key: "invite-user.email.default-text", params: { ...sender } };
    case InvitationType.BOARD_MEMBER:
      return { key: "invite-user.email.default-text-company-role", params: { ...sender } };
    default:
      return params.customRoleName !== undefined
        ? {
            key: "invite-user.email.default-text-custom-role",
            params: { ...sender, roleName: params.customRoleName },
          }
        : { key: "invite-user.email.default-text-administrator", params: { ...sender } };
  }
};
